import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Spin, message, Typography } from 'antd';
import { UserOutlined, TeamOutlined, TagOutlined, RocketOutlined } from '@ant-design/icons';
import api from '../services/api';
import { useAuth } from '../utils/auth';

const { Title } = Typography;

const Dashboard = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    customerCount: 0,
    activeGroups: 0,
    tagCount: 0,
    sopExecutionCount: 0
  });
  // 试用/许可信息
  const [trialInfo, setTrialInfo] = useState(null);
  const [licenseSummaryInline, setLicenseSummaryInline] = useState(null);

  const formatEpochSeconds = (sec) => {
    if (!sec || Number.isNaN(Number(sec))) return '-';
    const d = new Date(Number(sec) * 1000);
    return d.toLocaleString();
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const tenant = JSON.parse(localStorage.getItem('tenant') || '{}');
        const tenantId = tenant.id;

        if (!tenantId) {
          throw new Error('未找到租户信息');
        }

        // 并行请求所有数据
        const [customersRes, groupsRes, tagsRes, executionsRes, trialRes, licenseRes] = await Promise.all([
          api.get('/customers', {
            headers: { 'X-Tenant-ID': tenantId }
          }),
          api.get('/groups', {
            headers: { 'X-Tenant-ID': tenantId },
          }),
          api.get('/tags', {
            headers: { 'X-Tenant-ID': tenantId }
          }),
          api.get('/v1/sop/executions', {
            headers: { 'X-Tenant-ID': tenantId }
          }),
          api.get('/wework/licenses/trialInfo', { params: { tenantId } }),
          api.get('/wework/licenses/summary', { params: { tenantId } })
        ]);

        setStats({
          customerCount: customersRes.data.totalElements || 0,
          activeGroups: groupsRes.data.totalElements || 0,
          tagCount: tagsRes.data.totalElements || 0,
          sopExecutionCount: executionsRes.data.totalRecords || 0
        });

        setTrialInfo(trialRes.data || null);
        setLicenseSummaryInline(licenseRes.data || null);
      } catch (error) {
        console.error('获取仪表盘数据失败:', error);
        message.error('获取数据失败: ' + (error.response?.data?.message || error.message));
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4}>{user?.displayName || user?.username}，你好</Title>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="客户总数"
              value={stats.customerCount}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="活跃群组"
              value={stats.activeGroups}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="标签使用"
              value={stats.tagCount}
              prefix={<TagOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="推送执行数"
              value={stats.sopExecutionCount}
              prefix={<RocketOutlined />}
            />
          </Card>
        </Col>
      </Row>
      {/* 许可/试用信息卡片 */}
      {trialInfo && (() => {
        const licenseStatus = trialInfo.license_status; // 0: 未开启拦截
        const allow = trialInfo.allow_without_license;
        const start = trialInfo.trial_start_time;
        const end = trialInfo.trial_end_time;
        const checkTime = trialInfo.license_check_time;
        const used = licenseSummaryInline?.usedExternal;
        const available = licenseSummaryInline?.availableExternal;
        const expiringCnt = licenseSummaryInline?.expiringIn7DaysCount || 0;

        if (licenseStatus === 0) {
          return (
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Card>
                  <Statistic title="许可状态" value="未开启拦截" />
                </Card>
              </Col>
              {checkTime ? (
                <Col span={6}>
                  <Card>
                    <Statistic title="拦截校验开始时间" value={formatEpochSeconds(checkTime)} />
                  </Card>
                </Col>
              ) : null}
            </Row>
          );
        }

        if (allow) {
          const nowSec = Math.floor(Date.now() / 1000);
          const inTrial = start && end && nowSec >= start && nowSec < end;
          const remainDays = inTrial ? Math.ceil((end - nowSec) / 86400) : null;
          return (
            <Row gutter={16} style={{ marginBottom: 16 }}>
              {inTrial ? (
                <>
                  <Col span={6}>
                    <Card>
                      <Statistic title="试用到期" value={formatEpochSeconds(end)} />
                    </Card>
                  </Col>
                  <Col span={6}>
                    <Card>
                      <Statistic title="剩余天数" value={remainDays} />
                    </Card>
                  </Col>
                </>
              ) : (
                <Col span={6}>
                  <Card>
                    <Statistic title="拦截生效时间" value={checkTime ? formatEpochSeconds(checkTime) : '-'} />
                  </Card>
                </Col>
              )}
            </Row>
          );
        }

        // 试用结束且开启拦截：展示许可使用情况与许可到期、即将到期数
        return (
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={6}>
              <Card>
                <Statistic title="已用互通" value={used ?? '-'} />
              </Card>
            </Col>
            <Col span={6}>
              <Card>
                <Statistic title="可用互通" value={available ?? '-'} />
              </Card>
            </Col>
            {licenseSummaryInline?.latestExpireAt ? (
              <Col span={6}>
                <Card>
                  <Statistic title="许可到期" value={formatEpochSeconds(licenseSummaryInline.latestExpireAt)} />
                </Card>
              </Col>
            ) : null}
            <Col span={6}>
              <Card hoverable onClick={() => window.location.href = '/wework-employees'}>
                <Statistic title="7天内即将到期" value={expiringCnt} />
              </Card>
            </Col>
          </Row>
        );
      })()}
    </div>
  );
};

export default Dashboard; 
